/**
 * Query keys factory for Homepage module
 *
 * Provides structured query keys for TanStack Query.
 * Enables granular cache invalidation and consistent key management.
 *
 * @example
 * // In hooks
 * useQuery({ queryKey: homepageKeys.config() })
 *
 * // With storeId
 * useQuery({ queryKey: homepageKeys.config(storeId) })
 *
 * // For invalidation
 * queryClient.invalidateQueries({ queryKey: homepageKeys.all })
 */
export const homepageKeys = {
  /** Base key for all homepage queries */
  all: ['homepage'] as const,

  /** Homepage configuration (banners, sliders, promos) */
  config: (storeId?: number) =>
    storeId !== undefined
      ? [...homepageKeys.all, 'config', storeId] as const
      : [...homepageKeys.all, 'config'] as const,

  /** Banners only */
  banners: () => [...homepageKeys.all, 'banners'] as const,

  /** Product sliders only */
  sliders: () => [...homepageKeys.all, 'sliders'] as const,

  /** Promo blocks only */
  promoBlocks: () => [...homepageKeys.all, 'promoBlocks'] as const,

  /** Products by category */
  productsByCategory: (categoryId: string, pageSize: number) =>
    [...homepageKeys.all, 'products', 'category', categoryId, pageSize] as const,

  /** Products by SKU list */
  productsBySkus: (skus: string[], pageSize: number) =>
    [...homepageKeys.all, 'products', 'skus', skus.join(','), pageSize] as const,

  /** Category URLs by IDs */
  categoryUrls: (ids: string[]) =>
    [...homepageKeys.all, 'categoryUrls', ids.sort().join(',')] as const,
};
